import { BoardModel, CardModel, CardShift, GameModel, PileCardModel, PileModel } from "../interface/game";
import { shuffle, sortedFullDeck } from "./util/deck";
import { GameDescription } from "./game-description";
import { allGames } from "./games-database";
import { annotatePileCards } from "./pile-annotation";
import { PileAnnotationPolicy } from "./pile-annotation/pile-annotation-policy";
import { PileDropPolicy } from "./pile-drop/pile-drop-policy";
import { listBeforeAndΑfter } from "./util/list";
import { PileClickPolicy } from "./pile-click/pile-click-policy";
import { WinCondition } from "./win-condition/win-condition";

interface PilePolicies {
    annotations: PileAnnotationPolicy[];
    drop: PileDropPolicy;
    clickPolicy?: PileClickPolicy;
}

export class GameModelImpl implements GameModel {
    private board: BoardModel = {
        rows: 6,
        cols: 6,
        pileIds: []
    };
    private piles: {[key: string]: PileModel} = {};
    private cards: {[key: string]: CardModel} = {};
    private pilePolicies: {[key: string]: PilePolicies} = {};
    private pileHandlers: {[key: string]: (pile: PileModel) => void} = {};
    private gameStateHandler: (win: boolean) => void = () => {};
    private winCondition: WinCondition = () => false;

    private boardModelHandler: (board: BoardModel) => void;
    
    constructor() {
        this.boardModelHandler = () => {};
        this.setupBoard(allGames['Freecell']);
    }

    private notifyPileHandlers(pileIds: string[]) {
        for (const pileId of pileIds) {
            if (this.pileHandlers[pileId] !== undefined) {
                this.pileHandlers[pileId](this.piles[pileId]);
            }
        }
        if (this.winCondition(this.piles, this.cards)) {
            console.log('WIN');
            this.gameStateHandler(true);
        }
    }

    setupBoard(description: GameDescription): void {
        this.pilePolicies = {};
        this.cards = {};

        const piles: {[key: string]: PileModel} = {};
        const deck = shuffle(sortedFullDeck()).map((card, index) => ({
            ...card,
            id: `${index}`
        }));

        for (const card of deck) {
            this.cards[card.id] = {
                ...card,
                faceUp: false,
                shift: CardShift.CLOSED
            };
        }

        for (const pileDescription of description.piles) {
            const policies: PilePolicies = {
                annotations: pileDescription.annotationPolicies,
                drop: pileDescription.dropPolicy,
                clickPolicy: pileDescription.clickPolicy
            };
            this.pilePolicies[pileDescription.pileId] = policies;

            const pileCards = pileDescription.setup(deck, this.cards);

            piles[pileDescription.pileId] = {
                id: pileDescription.pileId,
                row: pileDescription.row,
                col: pileDescription.col,
                direction: pileDescription.direction,
                cards: annotatePileCards(policies.annotations, pileCards, this.cards),
                clickable: pileDescription.clickPolicy !== undefined,
            }
        }

        this.piles = piles;
        this.board = {
            rows: description.rows,
            cols: description.cols,
            pileIds: Object.keys(piles)
        };

        this.boardModelHandler({...this.board});
        this.winCondition = description.win;

        for (const pileId of Object.keys(this.pileHandlers)) {
            if (this.piles[pileId] !== undefined) {
                this.pileHandlers[pileId]({...this.piles[pileId]});
            } else {
                delete this.pileHandlers[pileId];
            }
        }

        console.log('Game Set Up!');
    }

    subscribeGameState(handler: (win: boolean) => void): void {
        this.gameStateHandler = handler;
    }

    subscribeBoardModel(handler: (board: BoardModel) => void): void {
        this.boardModelHandler = handler;
        handler({...this.board});
    }

    getPiledModel(pileId: string): PileModel {
        return this.piles[pileId];
    }

    subscribePileModel(pileId: string, handler: (pile: PileModel) => void): void {
        this.pileHandlers[pileId] = handler;
        handler({...this.piles[pileId]});
    }

    getCardModel(cardId: string): CardModel {
        return this.cards[cardId];
    }
    
    dropPile(destPileId: string, sourcePileId: string, cardId: string): boolean {
        if (destPileId === sourcePileId) {
            return false;
        }
        
        const destPile = this.piles[destPileId];
        const sourcePile = this.piles[sourcePileId];
        const policies = this.pilePolicies[destPileId];
        const sourceCards = listBeforeAndΑfter(sourcePile.cards, (card: PileCardModel) => card.id === cardId);
        if (policies.drop.canDrop(destPile.cards, sourceCards[1], this.cards)) {
            console.log(`Dropping`, sourceCards[1]);
            this.piles[destPileId] = {
                ...destPile,
                cards: annotatePileCards(policies.annotations, [...destPile.cards, ...sourceCards[1]], this.cards)
            };
            this.piles[sourcePileId] = {
                ...sourcePile,
                cards: annotatePileCards(this.pilePolicies[sourcePileId].annotations, sourceCards[0], this.cards)
            };
            this.notifyPileHandlers([sourcePileId, destPileId]);
            return true;
        }
        return false;
    }

    clickPile(pileId: string): void {
        const policy = this.pilePolicies[pileId].clickPolicy;
        if (policy !== undefined) {
            const pilesModified = policy(pileId, this.piles, this.cards);
            for (const modPileId of pilesModified) {
                const modPile = this.piles[modPileId];
                this.piles[modPileId] = {
                    ...modPile,
                    cards: annotatePileCards(this.pilePolicies[modPileId].annotations, modPile.cards, this.cards)
                };
            }
            this.notifyPileHandlers(pilesModified);
        }
    }
}